import { cx } from './cx';

/**
 * The console's icons, drawn inline.
 *
 * No icon font and no emoji: a font is a network request before the first paint and a
 * licence to check, and an emoji is a different picture on every operating system — on a
 * dispatch workstation running whatever Windows the office had, that is a coloured blob
 * beside a monochrome interface.
 *
 * Every path is on a 16px grid at stroke 1.75 with round caps, drawn in `currentcolor`, so
 * an icon inside a `Pill` or a `Button` is already the right colour and needs no variant.
 */
const PATHS = {
  phone:
    'M3.5 2.5h3l1.2 3-1.6 1.2a9 9 0 0 0 3.2 3.2l1.2-1.6 3 1.2v3a1 1 0 0 1-1.1 1A11.5 11.5 0 0 1 2.5 3.6a1 1 0 0 1 1-1.1Z',
  'phone-down':
    'M2 9.5a10 10 0 0 1 12 0M2 9.5v2.2l3-.6.2-2M14 9.5v2.2l-3-.6-.2-2',
  chevron: 'm4 6 4 4 4-4',
  'chevron-right': 'm6 4 4 4-4 4',
  check: 'm3 8.5 3.5 3.5L13 5',
  search: 'M7.3 12.1a4.8 4.8 0 1 0 0-9.6 4.8 4.8 0 0 0 0 9.6ZM11 11l3 3',
  'arrow-up': 'M8 13V3m0 0L4 7m4-4 4 4',
  'arrow-down': 'M8 3v10m0 0 4-4m-4 4-4-4',
  close: 'm4 4 8 8m0-8-8 8',
  back: 'M13 8H3m0 0 4-4M3 8l4 4',
  download: 'M8 2v8m0 0 3.5-3.5M8 10 4.5 6.5M2.5 13h11',
  upload: 'M8 10V2m0 0 3.5 3.5M8 2 4.5 5.5M2.5 13h11',
  pin: 'M8 10v4M5 2h6l-1 5 2 2H4l2-2-1-5Z',
  plus: 'M8 3v10M3 8h10',
  alert: 'M8 2.8 1.8 13.2h12.4L8 2.8Zm0 3.6v3.2m0 2.1v.1',
  info: 'M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Zm0-6.5V11m0-4.4v.1',
  /** A question mark in a ring: the mark a hint opens from. */
  help: 'M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12ZM6.2 6.4a1.9 1.9 0 1 1 2.7 1.8c-.6.3-.9.7-.9 1.2v.2M8 11.5v.1',
  clock: 'M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Zm0-9v3.4L10 10',
  car: 'M2.5 10.5h11M3.5 10.5v2h2v-2m5 0v2h2v-2M4 10.5 5.2 6h5.6L12 10.5M5 8.4h6',
  user: 'M8 8a2.6 2.6 0 1 0 0-5.2A2.6 2.6 0 0 0 8 8Zm-5 5.4a5 5 0 0 1 10 0',
  /** Two columns of three dots: the grip every resizable divider is known by. */
  grip: 'M6.3 4.2v.1m0 3.7v.1m0 3.7v.1M9.7 4.2v.1m0 3.7v.1m0 3.7v.1',
  /** Two arrows to opposite corners, the pair every map's enlarge button wears. */
  expand: 'M9.5 2.5h4v4m0-4L9 7M6.5 13.5h-4v-4m0 4L7 9',
  shrink: 'M14 6.5h-4v-4m4 4L9.5 2M2 9.5h4v4m-4-4L6.5 14',
  /** The recording player's: play, pause, the next and previous recording, ten seconds. */
  play: 'M5 3.2v9.6L12.5 8 5 3.2Z',
  pause: 'M5.5 3.5v9m5-9v9',
  'skip-back': 'M4 3.5v9m8-9v9L6 8l6-4.5Z',
  'skip-forward': 'M12 3.5v9m-8-9v9L10 8 4 3.5Z',
  rewind: 'M3.2 8.6A4.9 4.9 0 1 0 4.6 4.6M3.2 2.6v2.6h2.6',
  'fast-forward': 'M12.8 8.6a4.9 4.9 0 1 1-1.4-4M12.8 2.6v2.6h-2.6',
  trash: 'M2.5 4.5h11M6 4.5V2.8h4v1.7M4 4.5l.7 9h6.6l.7-9M6.7 7v4m2.6-4v4',
  /** A road between two places: a journey, and the way to its replay. */
  route: 'M4 14a1.6 1.6 0 1 0 0-3.2A1.6 1.6 0 0 0 4 14Zm8-8.8a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2ZM5.6 12.4h3.2a2.2 2.2 0 0 0 0-4.4H7.2a2.2 2.2 0 0 1 0-4.4h3.2',
  print: 'M4.5 6V2.5h7V6M4.5 11.5h-2v-5h11v5h-2M4.5 9.5h7v4h-7Z',
  /** A pencil: the mark on a value that is shown rather than typed, for the rare time it is changed by hand. */
  pencil: 'M11.2 2.8a1.4 1.4 0 0 1 2 2L5.6 12.4l-3.1 1.1 1.1-3.1 7.6-7.6Zm-1.4 1.4 2 2',
  /** Cross hairs in a ring: keep the map on the car. */
  follow: 'M8 13.5a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11ZM8 1v3m0 8v3M1 8h3m8 0h3',
} satisfies Record<string, string>;

export type IconName = keyof typeof PATHS;

export function Icon({
  name,
  small,
  className,
}: {
  name: IconName;
  /** 14px rather than 16, for a line of 12px text. */
  small?: boolean;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
      className={cx(
        small ? 'w-3.5 h-3.5 shrink-0' : 'w-4 h-4 shrink-0',
        'stroke-current fill-none',
        className,
      )}
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
